import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Paper, Stack, Avatar, CircularProgress, Alert, Tooltip, IconButton } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScoreIcon from '@mui/icons-material/Score';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const brandBlue = '#0B60B1';

const faqs = [
  {
    q: 'What is this tool for?',
    a: 'This tool helps you test the deliverability and spamminess of your emails before sending them to your real audience.'
  },
  {
    q: 'How do I use it?',
    a: 'Just follow the 3 steps above: copy the test email, send your email to it, and check your score.'
  },
  {
    q: 'What does the score mean?',
    a: 'A higher score means your email is more likely to land in the inbox. The tool checks for spam triggers, authentication, and other deliverability factors.'
  },
  {
    q: 'Is this free?',
    a: 'Yes, this tool is free to use for all users.'
  }
];

const getApiBaseUrl = () => (import.meta as any).env.VITE_API_BASE_URL || window.location.origin;
const getApiKey = () => (import.meta as any).env.VITE_API_KEY;

// Create axios instance with default config
const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Authorization': `Bearer ${getApiKey()}`
  }
});

export default function EmailTestPage() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(true);
  const [emailError, setEmailError] = useState<string | null>(null);

  useEffect(() => {
    const generateEmailId = async () => {
      try {
        setEmailLoading(true);
        const response = await api.get('/api/email-test/generate-id');
        if (response.data.success) {
          setTestEmail(response.data.data.full_email);
        } else {
          setEmailError('Failed to generate test email');
        }
      } catch (error: any) {
        setEmailError('Failed to generate test email');
        console.error('Error generating email ID:', error);
      } finally {
        setEmailLoading(false);
      }
    };

    generateEmailId();
  }, []);

  const handleCopy = () => {
    if (testEmail) {
      navigator.clipboard.writeText(testEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleCheckScore = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testEmail) return;

    // Extract email ID correctly - keep the full email ID including test+ prefix
    const emailId = testEmail.split('@')[0]; // This will be "test+randomid"
    console.log('Navigating to result page for ID:', emailId);
    
    // Navigate immediately to the result page
    navigate(`/tools/email-test-result/${emailId}`);
  };

  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh', pb: 10 }}>
      {/* Hero Section */}
      <Box sx={{ position: 'relative', bgcolor: brandBlue, color: '#fff', py: { xs: 3, md: 4 }, overflow: 'hidden', minHeight: { md: 80 } }}>
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Typography variant="h3" fontWeight={900} sx={{ letterSpacing: '-1.5px', mb: 1, fontSize: { xs: 22, md: 32 } }}>
            Email Spam & Deliverability Tester
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 0, fontWeight: 400, opacity: 0.95, maxWidth: 500, mx: 'auto' }}>
            Instantly check if your email will land in the inbox or spam folder.
          </Typography>
        </Container>
        {/* Subtle SVG background */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}>
          <svg width="100%" height="100%" viewBox="0 0 1440 320" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', top: 0, left: 0 }}>
            <path fill="#1976d2" fillOpacity="0.18" d="M0,160L60,170.7C120,181,240,203,360,197.3C480,192,600,160,720,133.3C840,107,960,85,1080,101.3C1200,117,1320,171,1380,197.3L1440,224L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z" />
          </svg>
        </Box>
      </Box>

      {/* Stepper Section */}
      <Container maxWidth="sm" sx={{ mt: 4, mb: 2 }}>
        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: '#e3eafc' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="center">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontWeight: 700 }}>1</Avatar>
              <Typography fontWeight={700} sx={{ fontSize: 16 }}>Copy the test email</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36, fontWeight: 700 }}>2</Avatar>
              <Typography fontWeight={700} sx={{ fontSize: 16 }}>Send your email/newsletter</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: '#388e3c', width: 36, height: 36, fontWeight: 700 }}>3</Avatar>
              <Typography fontWeight={700} sx={{ fontSize: 16 }}>Check your score</Typography>
            </Box>
          </Stack>
        </Paper>
      </Container>

      {/* Test Email Address Section */}
      <Container maxWidth="sm" sx={{ mb: 2 }}>
        <Paper elevation={6} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, textAlign: 'center', mb: 2, boxShadow: 6 }}>
          <Stack spacing={2} alignItems="center">
            <Typography variant="h6" fontWeight={700} color="primary" sx={{ mb: 1 }}>
              Your Test Email Address
            </Typography>
            {emailLoading ? (
              <CircularProgress size={24} />
            ) : emailError ? (
              <Alert severity="error">{emailError}</Alert>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, bgcolor: '#e3eafc', borderRadius: 2, px: 2, py: 1 }}>
                <EmailIcon color="primary" sx={{ mr: 1 }} />
                <Typography fontWeight={700} sx={{ fontFamily: 'monospace', fontSize: 18 }}>{testEmail}</Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                  <IconButton onClick={handleCopy} color={copied ? 'success' : 'primary'} size="small" sx={{ ml: 1 }}>
                    {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Paste this address in the "To" field of your email.
            </Typography>
          </Stack>
        </Paper>
      </Container>

      {/* Check Score Button */}
      <Container maxWidth="sm" sx={{ mb: 2 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={handleCheckScore}
            sx={{ fontWeight: 700, px: 5, py: 1.5, fontSize: 18, borderRadius: 3, boxShadow: 2 }}
            disabled={!testEmail}
            endIcon={<ScoreIcon />}
          >
            Check Score
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Click to view your email test result and score.
          </Typography>
        </Box>
      </Container>

      {/* Error Display */}
      {emailError && (
        <Container maxWidth="sm" sx={{ mt: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {emailError}
          </Alert>
        </Container>
      )}

      {/* FAQ/Help Section */}
      <Container maxWidth="md" sx={{ mt: 6, mb: 8 }}>
        <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 2 }}>
          <Typography variant="h5" fontWeight={700} textAlign="center" mb={3}>Email Test FAQ</Typography>
          {faqs.map((faq) => (
            <Accordion key={faq.q} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      </Container>
    </Box>
  );
} 